import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { askConfirm } from './confirmService';
import SheetSelect from './SheetSelect';

// ---------------------------------------------------------------------------
// Setup state persistence — sessionStorage (survives refresh, dies with tab).
//
// HARD RULE: storage is a HINT, never a dependency. Every read is wrapped in
// try/catch; every value is validated against the actual data before use.
// Corrupt, stale, or missing storage silently falls back to defaults. A user
// must NEVER need to clear their cache because of something we stored here.
// ---------------------------------------------------------------------------
const STORAGE_PREFIX = 'setup_';
const SENSITIVE_SETUP_PREFIXES = [STORAGE_PREFIX, 'dl_setup_draft_', 'dl_discover_draft_'];

const ssWrite = (key, value) => {
  try { sessionStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value)); } catch (_) { /* quota / private-mode — ignore */ }
};

const ssRead = (key, fallback) => {
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + key);
    if (raw == null) return fallback;
    return JSON.parse(raw);
  } catch (_) {
    return fallback;
  }
};

export const ssRemove = (key) => {
  try { sessionStorage.removeItem(STORAGE_PREFIX + key); } catch (_) { /* ignore */ }
};

export const clearSetupStorage = () => {
  const clearStore = (store) => {
    if (!store) return;
    const toRemove = [];
    for (let i = 0; i < store.length; i++) {
      const k = store.key(i);
      if (k && SENSITIVE_SETUP_PREFIXES.some((prefix) => k.startsWith(prefix))) toRemove.push(k);
    }
    toRemove.forEach((k) => store.removeItem(k));
  };
  try { clearStore(typeof sessionStorage !== 'undefined' ? sessionStorage : null); } catch (_) { /* ignore */ }
  try { clearStore(typeof localStorage !== 'undefined' ? localStorage : null); } catch (_) { /* ignore */ }
};

export const setupPersistedTab = (visibleIds) => {
  const stored = ssRead('tab', null);
  if (typeof stored === 'string' && visibleIds.includes(stored)) return stored;
  return null;
};

export const saveSetupTab = (id) => ssWrite('tab', id);

export const setupPersistedSelection = (tabId) => ssRead('sel_' + tabId, null);
export const saveSetupSelection = (tabId, value) => ssWrite('sel_' + tabId, value);

export const setupPersistedSubTab = (tabId) => ssRead('subtab_' + tabId, null);
export const saveSetupSubTab = (tabId, subTabId) => ssWrite('subtab_' + tabId, subTabId);

export const usePersistedSelection = (tabId, items, idExtractor, selectFn) => {
  const restoredRef = useRef(false);
  const mobilePortrait = useIsSetupMobilePortraitBase();
  useEffect(() => {
    if (restoredRef.current) return;
    // Mobile-PORTRAIT drill has no persistent list column, so do NOT restore the last-drilled record on
    // return — the user should land on the LIST, not be dropped back into a detail they didn't pick this
    // visit (jarring; forces a "back" tap to see the list). Split tiers keep the restore (the list stays
    // visible beside the detail). (#86bbfuajj 3P — mirrors useAutoSelectFirstInLandscape's portrait carve-out.)
    if (mobilePortrait) return;
    if (!items || !items.length) return;
    const stored = setupPersistedSelection(tabId);
    if (stored == null) return;
    const match = items.find((it) => idExtractor(it) === stored);
    if (!match) {
      ssRemove('sel_' + tabId);
      return;
    }
    restoredRef.current = true;
    selectFn(match);
    setTimeout(() => {
      const el = document.querySelector(
        '.setup-cam-row--active, .setup-user-card--active, .setup-list__row--active'
      );
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 0);
  }, [tabId, items, idExtractor, selectFn, mobilePortrait]);
};

export const useSaveSelection = (tabId, selectedId) => {
  const isFirst = useRef(true);
  useEffect(() => {
    if (isFirst.current) { isFirst.current = false; return; }
    if (selectedId != null && selectedId !== 'new') {
      saveSetupSelection(tabId, selectedId);
    }
  }, [tabId, selectedId]);
};

// Guard: confirm discarding unsaved edits before leaving or switching. Returns a PROMISE<boolean>
// (true = OK to proceed) — it now shows the house ConfirmModal via askConfirm instead of a native
// window.confirm, so callers must `await` it. Used by every org/camera screen on back + row-switch
// so edits are never lost silently.
export const confirmDiscardIfDirty = (dirty) =>
  dirty
    ? askConfirm({
      title: 'Discard changes?',
      message: 'You have unsaved changes. Discard them?',
      confirmLabel: 'Discard',
      cancelLabel: 'Keep editing',
      danger: true,
    })
    : Promise.resolve(true);

/**
 * Deep value-equality for setup form snapshots — scalars, nested plain objects (a camera's
 * `present` / a device's parsed `config`), and set-like primitive arrays (kept sorted at the
 * source, e.g. a user's site `access`). Every org/camera screen derives `dirty` by comparing the
 * live form against its PRISTINE snapshot rather than flipping a one-way "changed" flag — so
 * undoing an edit by hand (clear a motion grid back to its start, retype the original text) clears
 * dirty and stops nagging a Save with nothing to save (David 2026-07-21).
 */
export const formsEqual = (a, b) => {
  if (a === b) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a == null || b == null) return false;
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  return ak.every((k) => Object.prototype.hasOwnProperty.call(b, k) && formsEqual(a[k], b[k]));
};

// A screen reports its dirty state up to SetupView via this context; SetupView then guards
// navigation OUT of the screen (switching the top tab, Live, Logout) with the same discard prompt,
// closing the "I didn't think I edited anything" gap when leaving a dirty screen entirely.
//
// The same context also carries cross-tab navigation: `go(moduleId, params)` switches the active
// Setup tab (dirty-guarded) and hands the target module `params` it reads on mount — e.g. Device →
// "show THIS device's cameras" and, on the way back, Camera → "reselect THIS device". Screens not
// under a full provider (unit tests) get the safe no-op defaults.
// --- Setup edit-gating — THE shared mechanism for ALL cloud setup (rights-acl §2; David 2026-08-21) --
// bRole is a 0/1 MASTER BIT (there is NO role 2/3): 1 = Dividia master (edits everything), 0 = not.
// A field or section may declare `access: { master, local }` with each slot ∈ 'edit' | 'readonly' |
// 'hidden' — the slot names ARE the roles, so schemas read self-documenting. Everything defaults to
// master-edit / local-readonly; a later pass tags specific items to hide or lock. Reused from the POS
// work and now applied across every cloud setup screen (Customer / Site / Device / Camera / User).
export const isMasterRole = (bRole) => Number(bRole) === 1;
export const DEFAULT_ACCESS = { master: 'edit', local: 'readonly' };
// Normalize a field/item access descriptor to a { master, local } disposition pair. Accepts:
//   - a full { master, local } object (per-item granularity)
//   - a string shorthand: 'edit' (master edits, local read-only) | 'readonly' (read-only for all) |
//     'hidden' (hidden from all)
//   - absent → the master-edit default
// This is what replaced the old 'ro' / 'w' / 'adv' field flags (David 2026-08-21) — one mechanism.
export const normalizeAccess = (access) => {
  if (access == null) return DEFAULT_ACCESS;
  if (typeof access === 'object') return { master: access.master || 'edit', local: access.local || 'readonly' };
  if (access === 'readonly') return { master: 'readonly', local: 'readonly' };
  if (access === 'hidden') return { master: 'hidden', local: 'hidden' };
  return { master: 'edit', local: 'readonly' };   // 'edit' (or any writable shorthand)
};
// The current viewer's disposition ('edit' | 'readonly' | 'hidden') for a field/section.
export const resolveDisp = (item, isMaster) => normalizeAccess(item && item.access)[isMaster ? 'master' : 'local'];
// Screen-level gate: can this viewer edit setup? Master-only for now — every non-master (incl. local
// admins) is read-only until specific items are opened via `access`.
export const canEditSetup = (bRole) => isMasterRole(bRole);

// Per-device ADMIN (review #86bc63u9g F1). The coarse login `fAdmin` is the UNION of the user's grants
// (ADMIN on ANY device), so it can't decide whether THIS device/site is editable — cloudapi authorizes
// every org write per device (admin.js: rename/config need ADMIN on that device; a site edit needs ADMIN
// on ≥1 of its devices; a non-master site TIMEZONE change needs ADMIN on EVERY device of the site).
// `deviceRights` is the login's { sDeviceID → {fAdmin,…} } map (cloudSession.deviceRights). null (a
// pre-map backend) → fall back to the coarse flag so the UI keeps its old behavior; a device missing from
// a present map → not admin (fail closed; the server would 403 anyway).
export const deviceHasAdmin = (deviceRights, deviceId, fAdmin) => {
  if (!deviceRights) return !!fAdmin;
  const r = deviceRights[deviceId];
  return !!(r && (r.fAdmin === 1 || r.fAdmin === true));
};
// A site's admin coverage from the devices it owns: `any` = ADMIN on ≥1 (may edit name/timezone at all),
// `all` = ADMIN on every one (required by the server for a non-master timezone change). A site with no
// visible devices → neither.
export const siteAdminCoverage = (deviceRights, siteDeviceIds, fAdmin) => {
  const ids = siteDeviceIds || [];
  const admins = ids.filter((id) => deviceHasAdmin(deviceRights, id, fAdmin));
  return { any: admins.length > 0, all: ids.length > 0 && admins.length === ids.length };
};
// The server's own rejection text for a failed save (review F3): cloudapi answers a refused write with
// { success:false, error:'<why>' } — on a 4xx (axios rejects; body on e.response.data) or a 200. Returns
// that string, else the fallback.
export const serverErrorText = (errOrData, fallback) => {
  const body = errOrData && errOrData.response ? errOrData.response.data : errOrData;
  const msg = body && typeof body.error === 'string' ? body.error.trim() : '';
  return msg || fallback;
};

// A whole-subtree read-only lock (review F1): a non-master viewing a device they do NOT administer gets
// every Field rendered read-only regardless of its per-field access, without threading a flag through
// every tab. Only ever tightens.
export const SetupReadOnlyContext = createContext(false);

// Shared IANA timezone dropdown — used by BOTH the Site timezone picker AND the device global timezone
// (Config → Global) so they offer the identical selector. Guarded for jsdom / older engines (tests) → [];
// the current value is always kept present so a stored, non-listed zone never silently drops from the list.
export const TIMEZONES = (typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function')
  ? Intl.supportedValuesOf('timeZone') : [];
export const timezoneSelectOptions = (current) => {
  const list = (current && !TIMEZONES.includes(current)) ? [current, ...TIMEZONES] : TIMEZONES;
  return [{ value: '', label: '— select —' }, ...list.map((tz) => ({ value: tz, label: tz }))];
};

// Per-INPUT access gate — THE shared mechanism every setup input routes through (David 2026-08-24), so
// any single field can be re-gated later purely by data, never a blanket role check. `access` is the
// field's own descriptor ('edit' | 'readonly' | 'hidden' or a { master, local } pair); omitted → the
// standard master-edit / local-readonly default. Today: master edits everything, local reads everything
// (and can still SEE/open every screen). canEditField → may this viewer edit the field; isFieldHidden →
// should it be hidden from this viewer entirely.
export const canEditField = (isMaster, access) => resolveDisp({ access }, isMaster) === 'edit';
export const isFieldHidden = (isMaster, access) => resolveDisp({ access }, isMaster) === 'hidden';

// AccessField — THE single wrapper EVERY setup input routes through, so master/local × edit/read/hidden
// is specified per field by data with ONE mechanism (never a hand-rolled `disabled={!canManage}` per
// screen). Pass the field's own `access` — a { master, local } pair or a shorthand string
// ('edit' | 'readonly' | 'hidden'); omit it for the standard master-edit / local-readonly default.
// For THIS viewer: 'hidden' → renders nothing; otherwise renders a labelled Row and calls
// children(readOnly) with readOnly = (disp !== 'edit') so the input disables itself when read-only.
// Gate any single field later purely by giving it explicit access data. (David 2026-08-24)
export const AccessField = ({ access, isMaster, label, className, children }) => {
  const disp = resolveDisp({ access }, isMaster);
  if (disp === 'hidden') return null;
  return <Row label={label} className={className}>{children(disp !== 'edit')}</Row>;
};

// scale helpers for numeric fields that display a PERCENT but store a FRACTION (display = stored×scale,
// stored = entered÷scale); rounded so float noise never leaks. Only the numeric branch of Field uses them.
const scaleUp = (stored, scale) => { if (stored == null || stored === '') return ''; const n = Number(stored); return Number.isFinite(n) ? String(Math.round(n * scale)) : String(stored); };
const scaleDown = (entered, scale) => { if (entered == null || entered === '') return ''; const n = Number(entered); return Number.isFinite(n) ? Math.round(n) / scale : entered; };

// ─────────────────────────────────────────────────────────────────────────────────────────────────────
// Field — THE ONE setup input. There is exactly one input component in setup. You declare WHAT IT IS via
// `type` and it renders the matching widget; you declare WHO MAY TOUCH IT via `access` (from the single
// source) + `isMaster`, and Field routes that through the ONE gate (resolveDisp):
//     type:  'bool'   → switch        'number'/'num'/'int' → number input
//            'string'/'text'/'password' → text input        'enum'/'select' → dropdown (or segmented)
//     access → resolveDisp(access,isMaster):  'hidden' → renders nothing
//                                             'readonly' → the SAME widget, disabled
//                                             'edit'     → the SAME widget, enabled
// NOTHING about edit/readonly/hidden lives in render code anywhere — it is ALWAYS this one gate. TYPE and
// ACCESS are fully decoupled: the value's type NEVER implies its editability, and vice-versa. There is no
// fact/status/readonly input variant — a read-only field is just this widget, disabled. (David 2026-08-27)
// ─────────────────────────────────────────────────────────────────────────────────────────────────────
export const Field = ({
  type = 'string', label, value, access, isMaster, disp,
  onChange, options, hint, placeholder, min, max, step, suffix, scale, segmented, className, stacked, soleLabel,
  disabled, invalid, error, maxLength,
}) => {
  const locked = useContext(SetupReadOnlyContext);
  const d = disp != null ? disp : resolveDisp({ access }, isMaster);
  if (d === 'hidden') return null;                       // uniform: ACCESS 'hidden' renders nothing
  // ACCESS 'readonly' → the widget disabled. `disabled` is a separate, FUNCTIONAL lock (a dependent field
  // with nothing to act on yet, a single-option select, an in-flight save) — NOT an access decision; it
  // only ever tightens, never loosens, what the single source allows.
  const ro = d !== 'edit' || !!disabled || locked;
  const emit = (v) => { if (!ro && onChange) onChange(v); };

  if (type === 'bool') {
    const on = value === true || value === 1 || value === '1' || value === 'true' || value === 'on';
    return (
      <div className="setup-cfg-toggle">
        <label className={'setup-switch-row' + (ro ? ' setup-switch-row--disabled' : '')}>
          <span className={'setup-switch' + (on ? ' setup-switch--on' : '')}>
            <input type="checkbox" className="setup-switch__input" checked={on} disabled={ro}
              onChange={(e) => emit(e.target.checked)} />
            <span className="setup-switch__track"><span className="setup-switch__thumb" /></span>
          </span>
          <span className="setup-switch__label">{soleLabel ? 'Enabled' : label} <span className={'setup-switch__state' + (on ? ' setup-switch__state--on' : ' setup-switch__state--off')}>({on ? 'on' : 'off'})</span></span>
        </label>
      </div>
    );
  }

  if (type === 'enum' || type === 'select') {
    // Options are plain strings OR {value,label,hidden}. A `hidden` option is dropped from the list — UNLESS
    // it's the current value, which we always show (never silently drop/rewrite what the device has set).
    const norm = (options || []).map((o) => (typeof o === 'string'
      ? { value: o, label: o }
      : { value: String(o.value), label: o.label != null ? o.label : String(o.value), hidden: !!o.hidden }));
    const cur = value == null ? '' : String(value);
    const visible = norm.filter((o) => !o.hidden);
    if (cur !== '' && !visible.some((o) => o.value === cur)) {
      const known = norm.find((o) => o.value === cur);
      visible.unshift({ value: cur, label: known ? known.label : cur }); // never drop the stored value
    }
    // A read-only dropdown isn't interactive, so don't dress it as one: render the selected option's
    // LABEL as a plain disabled text field (no caret), matching every other read-only field. (#86bbm6j8w)
    if (ro && !segmented) {
      const curOpt = norm.find((o) => o.value === cur);
      const text = curOpt ? curOpt.label : cur;
      return (
        <Row label={label} className={className} wide={stacked}>
          <input className="setup-input setup-input--readonly" value={text == null || text === '' ? '—' : String(text)} disabled readOnly />
          {hint ? <div className="setup-hint">{hint}</div> : null}
        </Row>
      );
    }
    if (segmented) {
      return (
        <Row label={label} className={className} wide={stacked}>
          <div className="setup-theme-row setup-pos-segmented" role="group" aria-label={label}>
            {visible.map((o) => (
              <button key={o.value} type="button" className={'setup-theme-btn' + (cur === o.value ? ' setup-theme-btn--active' : '')}
                aria-pressed={cur === o.value} disabled={ro} onClick={() => emit(o.value)}>{o.label}</button>
            ))}
          </div>
          {hint ? <div className="setup-hint">{hint}</div> : null}
        </Row>
      );
    }
    return (
      <Row label={label} className={className} wide={stacked}>
        <SheetSelect className="setup-input" value={cur} disabled={ro} title={label} options={visible} onChange={(v) => emit(v)} />
        {hint ? <div className="setup-hint">{hint}</div> : null}
      </Row>
    );
  }

  const isNum = type === 'number' || type === 'num' || type === 'int';
  const shown = (isNum && scale) ? scaleUp(value, scale) : (value == null ? '' : String(value));
  const input = (
    <input className={'setup-input' + (ro ? ' setup-input--readonly' : '') + (suffix ? ' setup-input--suffixed' : '') + (invalid ? ' setup-input--invalid' : '')}
      type={isNum ? 'number' : (type === 'password' ? 'password' : 'text')}
      value={shown} disabled={ro} min={min} max={max} step={step} placeholder={placeholder} maxLength={maxLength}
      onChange={(e) => emit((isNum && scale) ? scaleDown(e.target.value, scale) : e.target.value)} />
  );
  return (
    <Row label={label} className={className} wide={stacked}>
      {suffix ? <div className="setup-pos-input-row">{input}<span className="setup-pos-suffix">{suffix}</span></div> : input}
      {/* An inline validation error (out-of-range / duplicate port) replaces the hint and reddens the input;
          when clean, the normal hint shows. (#86bbpgbdz) */}
      {error ? <div className="setup-field-error">{error}</div> : (hint ? <div className="setup-hint">{hint}</div> : null)}
    </Row>
  );
};

export const SetupNavContext = createContext({ reportDirty: () => {}, go: () => {}, params: null });
export const useReportDirty = (dirty) => {
  const { reportDirty } = useContext(SetupNavContext);
  useEffect(() => {
    reportDirty(dirty);
    return () => reportDirty(false);
  }, [dirty, reportDirty]);
};

// The mobile-landscape category (same query as setupStyle.css §4). This is a BEHAVIOR gate (which
// record is selected), not styling — CSS can't pick a list row — so a matchMedia check is the right
// tool here, distinct from the "no JS style-breakpoints" rule.
export const isSetupMobileLandscape = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' &&
  window.matchMedia('(orientation: landscape) and (max-height: 500px) and (max-width: 932px)').matches;

// Live-tracked variant of isSetupMobileLandscape: re-renders the caller when the viewport crosses the
// mobile-landscape threshold (rotate / resize), so a component can swap LAYOUT reactively — e.g. the
// Advanced overlay's accordion (portrait) ⇄ left-rail + content (landscape). Same behavior-gate
// rationale as isSetupMobileLandscape (CSS can't restructure an accordion into a rail). (#86bbfuajj)
export const useIsSetupMobileLandscape = () => {
  const [land, setLand] = useState(isSetupMobileLandscape);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia('(orientation: landscape) and (max-height: 500px) and (max-width: 932px)');
    const onChange = () => setLand(mql.matches);
    onChange();
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else if (mql.addListener) mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else if (mql.removeListener) mql.removeListener(onChange);
    };
  }, []);
  return land;
};

// In the mobile-landscape SPLIT (list + detail visible together), auto-select the first list item on
// first load so the detail pane shows data instead of an empty "Select an item…" prompt. Portrait is
// a one-pane drill (you want the list first), so this is landscape-ONLY. Fires once, only when nothing
// is selected yet and rows exist.
export const useAutoSelectFirst = (items, hasSelection, selectFirst) => {
  const doneRef = useRef(false);
  useEffect(() => {
    if (doneRef.current || hasSelection) return;
    if (!items || !items.length) return;
    doneRef.current = true;
    selectFirst(items[0]);
  }, [items, hasSelection, selectFirst]);
};

// Mobile-PORTRAIT base is the only one-pane drill — every wider tier (tablet portrait/landscape,
// desktop) and mobile-landscape shows list + detail together. Auto-select belongs only in those
// split layouts; on the portrait drill the user should land on the LIST, not be dropped into a
// detail they didn't pick. (#86bbfuajj 3P)
export const isSetupMobilePortraitBase = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' &&
  window.matchMedia('(orientation: portrait) and (max-width: 767px)').matches;

// Track the one-pane portrait drill. Selection hooks must retry when rotation changes the screen to
// a split layout, where a detail selection belongs beside the still-visible list.
export const useIsSetupMobilePortraitBase = () => {
  const [portrait, setPortrait] = useState(isSetupMobilePortraitBase);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia('(orientation: portrait) and (max-width: 767px)');
    const onChange = () => setPortrait(mql.matches);
    onChange();
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else if (mql.addListener) mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else if (mql.removeListener) mql.removeListener(onChange);
    };
  }, []);
  return portrait;
};

// Auto-select the first row ONLY in a split layout. On mobile-portrait base we pass hasSelection=true
// so the hook no-ops and the list loads bare. (matchMedia is absent/false under jsdom → tests keep the
// split behavior, exercising the auto-select path.)
export const useAutoSelectFirstInLandscape = (items, hasSelection, selectFirst) => {
  const mobilePortrait = useIsSetupMobilePortraitBase();
  useAutoSelectFirst(items, hasSelection || mobilePortrait, selectFirst);
};

/**
 * setupCommon — the shared Setup aesthetic + master-detail chrome for the cloud org modules
 * (Customers / Sites / Devices), extracted so every cloud Setup screen reads as ONE surface.
 *
 * The vocabulary mirrors the Java dview setup (labelled fields, uppercase section headers,
 * read-only facts as disabled fields, a list ▸ detail flow) and matches the other cloud Setup
 * screens, so Customers/Sites/Devices/Users/Cameras look like siblings.
 *
 * Styling is className-driven (setupStyle.css, scoped under `.setup-container`) per the house
 * design system (docs/ui-styling-guidelines.md). Responsiveness lives entirely in the stylesheet:
 * the master-detail stacks on mobile portrait and goes side-by-side at larger sizes via a CSS
 * structural query — NOT a JS breakpoint. Import these *components* rather than hand-rolling the
 * divs so every screen inherits the same layout + responsive behavior.
 */

// Labelled field row (Java's setup layout); label-over-field on mobile, label-beside-field on
// larger screens — controlled by the .setup-form__row structural query in setupStyle.css.
export const Row = ({ label, className, wide, children }) => (
  <div className={'setup-form__row' + (wide ? ' setup-form__row--wide' : '') + (className ? ` ${className}` : '')}>
    <div className="setup-form__label">{label}</div>
    <div className="setup-form__field">{children}</div>
  </div>
);

// The master-detail container: stacked on mobile, side-by-side at larger sizes (CSS-driven).
export const SetupCols = ({ children }) => <div className="setup-cols">{children}</div>;

// The right-hand detail column.
export const SetupDetail = ({ children }) => <div className="setup-detail">{children}</div>;

/**
 * SetupDrill — the shared mobile master-detail DRILL-DOWN shell (the pattern locked on Customers,
 * David 2026-07-21). List and detail are separate full-height views: picking a row swaps to the
 * detail with a "‹ <plural>" back link, the saved record name as a heading, and a sticky-after-
 * content action bar. Driven by selection state (`detailOpen`), not a viewport width. CSS lives in
 * setupStyle.css under `.setup-drill*`; the desktop split-view will layer on as a structural query.
 *
 * Props:
 *   detailOpen  — is a record selected (show the detail) vs. the list
 *   list        — the master list node (typically <SetupListPanel searchFirst>…rows…</SetupListPanel>)
 *   backLabel   — the PLURAL section the back link returns to ("Customers"); back links stay plural
 *   title       — the detail heading (the SAVED record name, or "New …") — pass a stable value so it
 *                 doesn't track the Name field as you type
 *   onBack      — leave the detail, back to the list
 *   headingAction — optional control right-aligned ON the heading line (a record-scoped action such
 *                 as Reset Password); omit for none. The heading text ellipsizes to make room for it.
 *   headingNav  — optional nav (the detail's General/Config/… tab bar) rendered under the name. In
 *                 mobile landscape it rides ON the name row to reclaim a row of the short pane; on
 *                 every other viewport it sits full-width just below the name (where it always was).
 *   actions     — the sticky action-bar contents (Save / Revert / Delete); omit for read-only
 *   children    — the detail body (form fields). Guard with `detailOpen ? … : null` in the caller so
 *                 field refs don't evaluate a null form.
 */
export const SetupDrill = ({ detailOpen, list, backLabel, title, onBack, accessory, headingAction, headingNav, actions, fillBody, wideList, bare, children }) => (
  <div className={'setup-screen setup-drill' + (detailOpen ? ' setup-drill--detail' : ' setup-drill--list') + (wideList ? ' setup-drill--wide-list' : '')}>
    <div className="setup-drill__list">{list}</div>
    <div className="setup-drill__detail">
      {detailOpen ? (
        <>
          {/* `bare` lets a screen own its own detail chrome (e.g. the POS add/modify screen renders its
              own breadcrumb + device header + action title) — skip the drill's bar but keep body + actions. */}
          {bare ? null : (
          <div className="setup-drill__bar">
            <div className="setup-drill__bar-top">
              <button className="setup-drill__back" onClick={onBack}>{'‹ ' + backLabel}</button>
              {accessory ? <div className="setup-drill__bar-accessory">{accessory}</div> : null}
            </div>
            <div className="setup-drill__heading-row">
              <div className="setup-drill__heading">{title}</div>
              {headingAction ? <div className="setup-drill__heading-action">{headingAction}</div> : null}
            </div>
            {headingNav ? <div className="setup-drill__heading-nav">{headingNav}</div> : null}
          </div>
          )}
          <div className={'setup-drill__body' + (fillBody ? ' setup-drill__body--fill' : '')}>{children}</div>
          {actions ? <div className="setup-drill__actions">{actions}</div> : null}
        </>
      ) : null}
    </div>
  </div>
);

// An inline status/type pill. The color is runtime-derived (family / online state), so it stays
// an inline background — the one legitimate dynamic-style exception (docs/ui-styling-guidelines §5).
export const Badge = ({ color, children, title }) => (
  <span className="setup-badge" style={color ? { background: color } : undefined} title={title}>{children}</span>
);

// A single row in the left master list. `active` highlights the selection.
// `accessories` renders inline pills/badges after the name; `meta` renders a right-aligned
// secondary line (timestamp, status) beneath the sub line.
export const ListRow = ({ active, onClick, name, sub, accessories, meta }) => (
  <div className={'setup-list__row' + (active ? ' setup-list__row--active' : '')} onClick={onClick}>
    <div className="setup-list__name-line">
      <span className="setup-list__name">{name}</span>
      {accessories ? <span className="setup-list__accessories">{accessories}</span> : null}
    </div>
    {(sub || meta) ? (
      <div className="setup-list__details">
        {sub ? <span className="setup-list__sub">{sub}</span> : null}
        {meta ? <span className="setup-list__meta">{meta}</span> : null}
      </div>
    ) : null}
  </div>
);

// The left master column: an optional New button, a filter box, and a scrollable list of rows
// (children). Full-width on mobile; a bounded, fixed column at larger sizes (CSS-driven).
// `searchFirst` puts the filter above the New button — finding/editing an existing record is the
// everyday action, adding is occasional, so search leads.
export const SetupListPanel = ({
  newLabel, onNew, filter, setFilter, filterPlaceholder, loading, empty, emptyText, searchFirst, subhead, children,
}) => {
  const search = (
    <div className="setup-list__search-wrap">
      <input
        className="setup-list__search"
        placeholder={filterPlaceholder || 'Filter…'}
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      />
      {filter ? (
        <button
          type="button"
          className="setup-list__search-clear"
          onClick={() => setFilter('')}
          aria-label="Clear filter"
          title="Clear filter"
        >
          ×
        </button>
      ) : null}
    </div>
  );
  const newBtn = onNew ? (
    <button
      className={'setup-list__new' + (searchFirst ? ' setup-list__new--compact' : '')}
      onClick={onNew}
    >
      {newLabel || '+ New'}
    </button>
  ) : null;
  return (
    <div className="setup-list">
      {searchFirst
        ? <div className="setup-list__head">{search}{newBtn}</div>
        : <>{newBtn}{search}</>}
      {subhead ? <div className="setup-list__subhead">{subhead}</div> : null}
      <div className="setup-list__scroll">
        {loading ? <div className="setup-muted">Loading…</div> : null}
        {!loading && empty ? <div className="setup-muted">{emptyText || 'Nothing here.'}</div> : null}
        {children}
      </div>
    </div>
  );
};

// A customer + site sub-label for list rows, collapsed to one value when the two are the same
// text (case-insensitive) — avoids "dragon · dragon". Returns '' when both are empty.
export const orgSubLabel = (customer, site) => {
  const c = (customer || '').trim();
  const p = (site || '').trim();
  if (c && p) return c.toLowerCase() === p.toLowerCase() ? c : `${c} · ${p}`;
  return c || p || '';
};

// A read-only fact shown as a disabled field (Java + isite show Device ID / IP / etc. this way).
export const ReadOnlyRow = ({ label, value, hint, wide }) => (
  <Row label={label} wide={wide}>
    <input
      className="setup-input setup-input--readonly"
      value={value == null || value === '' ? '—' : String(value)}
      disabled
      readOnly
    />
    {hint ? <div className="setup-hint">{hint}</div> : null}
  </Row>
);

// Coerce a text-input value toward a number when the field is numeric: a finite number goes back as a
// NUMBER (so the config keeps its native JSON type live), anything else (empty, partial, non-numeric)
// stays a string and reconcileTypes() is the save-time backstop. (David 2026-08-27)
const numOrStr = (v) => { const n = Number(v); return (v !== '' && v != null && Number.isFinite(n)) ? n : v; };
// The widget type for a schemaless config key, inferred from its JSON value (it has no declared type).
export const rawType = (raw) => (typeof raw === 'boolean' ? 'bool' : (typeof raw === 'number' ? 'number' : 'string'));

// UnknownField — NOT another input: it is the ONE Field, wrapped in a 4-line adapter for config keys the
// schema does NOT model. Such a key has no DECLARED type, so we infer it from the JSON value and FREEZE it
// on mount (so it can't flip mid-edit), then hand off to Field. Access still comes entirely from the single
// source via `disp`. onChange(section, key, value) writes into the config blob (setEngineKey/setConnectorKey).
export const UnknownField = ({ label, entry, onChange, disp }) => {
  const typeRef = useRef(rawType(entry.raw));
  const t = typeRef.current;
  return (
    <Field type={t} disp={disp} label={label} value={entry.value}
      onChange={(v) => onChange && onChange(entry.section, entry.key, t === 'number' ? numOrStr(v) : v)} />
  );
};

// A group listing config keys the viewer's schema does NOT model, so "nothing the engine knows about is
// hidden" (the boil-the-lake completeness rule). `entries` = [{key,value,raw}] (camera) or
// [{section,key,value,raw}] (device). EVERY entry is a managed input under the SAME uniform 3-state rule:
// `dispOf(entry)` (from the single source; default master:edit / local:readonly) decides hidden/readonly/
// edit — there is no separate read-only widget. Edits ride in the blob (writeCamera/writeDevice preserve
// unknown keys). Renders nothing when every entry is hidden or the list is empty.
export const OtherSettings = ({ title, entries, onChange, dispOf }) => {
  if (!entries || !entries.length) return null;
  const disp = (e) => (typeof dispOf === 'function' ? dispOf(e) : 'edit');
  const rows = entries
    .map((e, i) => ({ e, i, d: disp(e) }))
    .filter((x) => x.d !== 'hidden')                        // uniform: hidden entries render nothing
    .map(({ e, i, d }) => (
      <UnknownField key={(e.section ? `${e.section}.` : '') + e.key + i}
        label={(e.section ? `${e.section} · ` : '') + e.key} entry={e} onChange={onChange} disp={d} />
    ));
  if (!rows.length) return null;
  return (
    <>
      <div className="setup-section">{title || 'Additional settings'}</div>
      {/* 2-column at landscape (plain block in portrait) */}
      <div className="setup-other-grid">{rows}</div>
    </>
  );
};
